import React, {Fragment} from 'react';
import {Percent} from '~/library/types';
import {color} from '../../Theme';

interface StyleProps {
    thickness?: number;
    gap?: number;
}
interface Props extends StyleProps {
    parts: Array<DataPart>;
    className?: string;
}

const VIEW_BOX = 100;
const RADIUS = VIEW_BOX / 2;

export function RsnBublikChart({parts, thickness = 16, gap = 1, ...props}: Props) {
    const bublik = Bublik.from(RADIUS, thickness);

    const sections = parts.reduce<DataSection[]>((all, nxt, idx) => {
        let startPercent = Percent.zero;
        if (all[idx - 1]) {
            startPercent = all[idx - 1].endPercent;
        }
        const endPercent = Percent(startPercent + nxt.percent);
        all.push({color: nxt.color, startPercent, endPercent});
        return all;
    }, []);

    const last = sections.at(-1);
    if (!last) {
        return renderSvg(renderRestPart(Percent.zero));
    }

    const bublikIsIncomplete = 1 - last.endPercent >= Number.EPSILON;

    return renderSvg(
        <Fragment>
            {sections.map((part, idx) => (
                <path
                    key={idx}
                    d={bublik.getPartPath(part.startPercent, part.endPercent).toString()}
                    fill={part.color}
                    strokeWidth={gap}
                    stroke={color.neutral20}
                />
            ))}
            {bublikIsIncomplete && renderRestPart(last.endPercent)}
        </Fragment>
    );

    function renderSvg(children: React.ReactNode) {
        return (
            <svg viewBox={`0 0 ${VIEW_BOX} ${VIEW_BOX}`} width='100%' height='100%' className={props.className}>
                {children}
            </svg>
        );
    }

    function renderRestPart(start: Percent) {
        return (
            <path
                d={bublik.getPartPath(start, Percent.hundred).toString()}
                fill={color.neutral40}
                strokeWidth={gap}
                stroke={color.neutral20}
            />
        );
    }
}

interface PropsWithData extends StyleProps {
    data: DataPoint[];
    total?: number;
}

RsnBublikChart.partsFromData = partsFromData;
function partsFromData({total, data}: Omit<PropsWithData, keyof StyleProps>) {
    total = total || data.reduce((sum, nxt) => sum + nxt.value, 0);
    const parts = data.reduce<DataPart[]>((all, nxt) => {
        all.push({
            color: nxt.color,
            percent: Percent(nxt.value / total),
        });
        return all;
    }, []);
    return parts;
}

RsnBublikChart.FromData = FromData;
FromData.displayName = 'RsnBublikChart.FromData';
function FromData({data, total, ...styleProps}: PropsWithData) {
    return <RsnBublikChart {...styleProps} parts={partsFromData({data, total})} />;
}

interface DataStyle {
    color: string;
}
interface DataPoint extends DataStyle {
    value: number;
}
interface DataPart extends DataStyle {
    percent: Percent;
}
interface DataSection extends DataStyle {
    startPercent: Percent;
    endPercent: Percent;
}

class Point {
    constructor(
        readonly x: number,
        readonly y: number,
        precision = 2
    ) {
        const k = 10 ** precision;
        this.x = Math.trunc(x * k) / k;
        this.y = Math.trunc(y * k) / k;
    }

    add(other: Point) {
        return new Point(this.x + other.x, this.y + other.y);
    }

    toString() {
        return `${this.x},${this.y}`;
    }
}

class Circle {
    static fullRadians = Math.PI * 2;

    constructor(
        readonly center: Point,
        readonly radius: number,
        readonly origin = Circle.Origin.north
    ) {}

    getPointAt(percent: Percent) {
        const angle = Circle.fullRadians * percent + this.origin;
        const hypotenuse = this.radius;
        const x = hypotenuse * Math.cos(angle);
        const y = hypotenuse * Math.sin(angle);
        return new Point(x, y).add(this.center);
    }

    getCircumference() {
        return 2 * Math.PI * this.radius;
    }
}
namespace Circle {
    export enum Origin {
        north = -Math.PI * 0.5,
        east = 0,
        south = Math.PI * 0.5,
        west = Math.PI,
    }
}

class Bublik {
    static from(radius: number, thickness: number) {
        const center = new Point(radius, radius);
        const circle = new Circle(center, radius);
        const bublik = new Bublik(circle, thickness);
        return bublik;
    }

    readonly innerCircle: Circle;

    constructor(
        readonly outerCircle: Circle,
        readonly thickness: number
    ) {
        this.innerCircle = new Circle(outerCircle.center, outerCircle.radius - thickness);
    }

    getPartPath(startPercent: Percent, endPercent: Percent) {
        const {innerCircle: inner, outerCircle: outer} = this;
        let middle: Percent | undefined;
        const isLongArc = endPercent - startPercent > 0.5;
        if (isLongArc) {
            middle = Percent(endPercent / 2);
        }
        const start = outer.getPointAt(startPercent);
        const path = new PathBuilder();
        path.move(start);
        if (middle) {
            path.arc(outer.radius, outer.getPointAt(middle), true);
        }
        path.arc(outer.radius, outer.getPointAt(endPercent), true);
        path.line(inner.getPointAt(endPercent));
        if (middle) {
            path.arc(inner.radius, inner.getPointAt(middle), false);
        }
        path.arc(inner.radius, inner.getPointAt(startPercent), false);
        path.line(start);
        return path;
    }
}

class PathBuilder {
    private path = '';

    move(point: Point) {
        this.path += `M${point} `;
        return this;
    }

    arc(r: number, end: Point, sweep = false, long = false, rotation = 0) {
        this.path += `A${[r, r, rotation, Number(long), Number(sweep), end].join(',')} `;
        return this;
    }

    line(point: Point) {
        this.path += `L${point} `;
        return this;
    }

    toString() {
        return this.path;
    }
}
